import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { backend_url } from "../App";
import { PRODUCT_TABS_DEFAULTS as D, PRODUCT_TABS_LIMITS as L } from "../Constants/productTabsDefaults";
import est1 from "../Components/Assets/est-1.jpeg";
import est2 from "../Components/Assets/est-2.jpeg";
import SaveBar from "../Components/SaveBar/SaveBar";
import "./CSS/AboutFabricEditor.css"; // shared editor look (fields, counters, sections)
import "./CSS/ProductTabsEditor.css";

const KEY = "product-tabs";

// Only fields that differ from the original are saved
const changesOnly = (values) => {
  const out = {};
  Object.keys(D).forEach((k) => { if (JSON.stringify(values[k]) !== JSON.stringify(D[k])) out[k] = values[k]; });
  return out;
};
const withDefaults = (saved) => {
  const out = { ...D };
  Object.entries(saved || {}).forEach(([k, v]) => {
    if (!(k in D)) return;
    if (typeof v === "boolean") out[k] = v;
    else if (Array.isArray(v) ? v.length : typeof v === "string" && v.trim()) out[k] = v;
  });
  return out;
};
const authHeaders = (json = true) => ({
  ...(json ? { "Content-Type": "application/json" } : {}),
  Authorization: `Bearer ${sessionStorage.getItem("adminToken")}`,
});

const TAB_DEFS = [
  { id: "description", n: 1, title: "Description", sub: "Product details table — filled from each product" },
  { id: "estimator", n: 2, title: "Fabric Estimator", sub: "Text, button and the pop-up with two picture guides" },
  { id: "shipping", n: 3, title: "Shipping", sub: "Delivery heading, text and an optional picture" },
  { id: "washcare", n: 4, title: "Washcare", sub: "Care tips list, link and an optional picture" },
];

const ProductTabsEditor = () => {
  const [values, setValues] = useState(D);
  const [saved, setSaved] = useState(D);
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [open, setOpen] = useState({ estimator: true });
  const [previewTab, setPreviewTab] = useState("estimator");
  const [confirm, setConfirm] = useState(null);
  const fileRef = useRef();
  const uploadFor = useRef("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backend_url}/api/site-content/${KEY}`);
      const data = await res.json().catch(() => ({}));
      const v = withDefaults(data.success ? data.data : null);
      setValues(v); setSaved(v);
      setIsCustom(!!(data.success && data.data && Object.keys(data.data).length));
      if (res.status === 404) toast.info("Showing the original content. Saving needs the latest backend to be deployed.");
    } catch {
      setValues(D); setSaved(D);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = JSON.stringify(values) !== JSON.stringify(saved);
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const editedCount = useMemo(() => Object.keys(changesOnly(values)).length, [values]);

  const warnings = useMemo(() => {
    const w = {};
    if (values.estimatorNoteBold && !values.estimatorNote.includes(values.estimatorNoteBold))
      w.estimatorNoteBold = "These words aren't in the note, so nothing will be bold. Copy them exactly.";
    Object.keys(L).forEach((k) => {
      const v = values[k];
      const empty = Array.isArray(v) ? !v.some((x) => x.trim()) : !String(v ?? "").trim();
      if (empty && k !== "estimatorNoteBold") w[k] = "Empty — the original text will be shown instead.";
    });
    return w;
  }, [values]);

  const cleanLists = (v) => ({ ...v, washcareTips: v.washcareTips.map((x) => x.trim()).filter(Boolean) });

  const save = async () => {
    setSaving(true);
    const sent = changesOnly(cleanLists(values));
    try {
      const res = await fetch(`${backend_url}/api/site-content/${KEY}`, {
        method: "PUT", headers: authHeaders(), body: JSON.stringify({ data: sent }),
      });
      if (res.status === 404) return { ok: false, message: "The server doesn't have this feature yet — deploy the latest backend, then save again." };
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 || data.message === "Invalid Token") return { ok: false, message: "Your login has expired — please log in again, then save." };
      if (!data.success) return { ok: false, message: data.message || "Could not save" };
      // An outdated server silently ignores fields it doesn't know — say so instead of "Saved!"
      const ignored = Object.keys(sent).filter((k) => !(k in (data.data || {})));
      if (ignored.length) return { ok: false, message: `The server didn't save: ${ignored.join(", ")}. Restart or deploy the latest backend, then save again.` };
      const v = withDefaults(data.data);
      setValues(v); setSaved(v);
      setIsCustom(Object.keys(data.data || {}).length > 0);
      return { ok: true };
    } catch {
      return { ok: false, message: "Could not reach the server — check your connection and try again." };
    } finally {
      setSaving(false);
    }
  };

  const pickImage = (field) => { uploadFor.current = field; fileRef.current?.click(); };
  const uploadImage = async (file) => {
    const field = uploadFor.current;
    if (!file || !field) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch(`${backend_url}/api/site-content-image`, { method: "POST", headers: authHeaders(false), body: fd });
      if (res.status === 404) { toast.error("The server doesn't have this feature yet — deploy the latest backend first."); return; }
      const data = await res.json().catch(() => ({}));
      if (data.success) { set(field, data.url); toast.success("Picture uploaded — save to put it on the website"); }
      else toast.error(data.message === "Invalid Token" ? "Your login has expired — please log in again." : data.message || "Upload failed");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resetAll = () => setConfirm({
    title: "Go back to the original tabs?", text: "All your changes to the product page tabs are removed from the website.", yes: "Reset tabs",
    onYes: async () => {
      try {
        const res = await fetch(`${backend_url}/api/site-content/${KEY}`, { method: "DELETE", headers: authHeaders() });
        const data = await res.json().catch(() => ({}));
        if (data.success) { toast.success("Tabs reset to the original"); load(); } else toast.error(data.message || "Could not reset");
      } catch { toast.error("Could not reset"); }
    },
  });

  /* ── field helpers (called as functions so inputs keep focus) ── */
  const counter = (len, max) => (
    <span className={`af-counter ${len >= max ? "full" : len >= max * 0.9 ? "near" : ""}`}>{len}/{max}</span>
  );
  const Edited = (k) => JSON.stringify(values[k]) !== JSON.stringify(D[k]) ? (
    <button type="button" className="af-reset-field" onClick={() => set(k, D[k])} title="Put back the original">Edited · Reset</button>
  ) : null;
  const Field = ({ k, label, hint, area, rows = 3 }) => (
    <div className="af-field">
      <div className="af-field-top"><label htmlFor={`pt-${k}`}>{label}</label>{Edited(k)}</div>
      {area
        ? <textarea id={`pt-${k}`} rows={rows} maxLength={L[k]} value={values[k]} onChange={(e) => set(k, e.target.value)} />
        : <input id={`pt-${k}`} maxLength={L[k]} value={values[k]} onChange={(e) => set(k, e.target.value)} />}
      <div className="af-field-foot">
        {warnings[k] ? <p className="af-warn">⚠ {warnings[k]}</p> : hint ? <p className="af-hint">{hint}</p> : <span />}
        {counter(String(values[k] ?? "").length, L[k])}
      </div>
    </div>
  );
  const Toggle = ({ k, label }) => (
    <label className="pt-toggle">
      <button type="button" role="switch" aria-checked={values[k]} aria-label={label}
        className={`pt-switch ${values[k] ? "on" : ""}`} onClick={() => set(k, !values[k])}><span /></button>
      <span>{values[k] ? "Shown on product pages" : "Hidden"}</span>
    </label>
  );
  const ImageField = ({ k, label, fallback, hint }) => (
    <div className="af-field">
      <div className="af-field-top"><label>{label}</label>{Edited(k)}</div>
      <div className="af-image">
        {values[k] || fallback ? <img src={values[k] || fallback} alt="" /> : <div className="pt-noimg">No picture</div>}
        <div className="af-image-actions">
          <p className="af-hint">{values[k] ? "Your uploaded picture" : fallback ? "The original picture" : "No picture — the tab shows text only"}</p>
          <button type="button" className="af-btn soft" disabled={!!uploading} onClick={() => pickImage(k)}>
            {uploading === k ? "Uploading…" : values[k] ? "Change picture" : "Upload picture"}
          </button>
          {values[k] && (
            <button type="button" className="af-link" onClick={() => set(k, "")}>
              {fallback ? "Use the original picture" : "Remove picture"}
            </button>
          )}
          {hint && <p className="af-hint">{hint}</p>}
        </div>
      </div>
    </div>
  );
  const Section = (id, children, toggleKey) => {
    const s = TAB_DEFS.find((t) => t.id === id);
    const isOpen = !!open[id];
    return (
      <section className={`af-section ${isOpen ? "open" : ""} ${previewTab === id ? "focused" : ""} ${toggleKey && !values[toggleKey] ? "pt-off" : ""}`}>
        <button type="button" className="af-section-head" onClick={() => { setOpen((o) => ({ ...o, [id]: !o[id] })); setPreviewTab(id); }}>
          <span className="af-num">{s.n}</span>
          <span className="af-section-text">
            <span className="af-section-title">{s.title}{toggleKey && !values[toggleKey] && <em className="pt-hidden-pill">Hidden</em>}</span>
            <span className="af-section-sub">{s.sub}</span>
          </span>
          <span className="af-chevron">{isOpen ? "−" : "+"}</span>
        </button>
        {isOpen && <div className="af-section-body" onFocusCapture={() => setPreviewTab(id)}>{children}</div>}
      </section>
    );
  };

  if (loading) return <div className="af-page"><p className="af-muted">Loading…</p></div>;
  const v = values;
  const pv = (k) => (String(v[k] ?? "").trim() ? v[k] : D[k]);
  const previewTabs = [
    { id: "description", label: pv("descTab") },
    v.showEstimator && { id: "estimator", label: pv("estimatorTab") },
    v.showShipping && { id: "shipping", label: pv("shippingTab") },
    v.showWashcare && { id: "washcare", label: pv("washcareTab") },
  ].filter(Boolean);
  const shown = previewTabs.some((t) => t.id === previewTab) ? previewTab : "description";
  const note = pv("estimatorNote");
  const boldAt = v.estimatorNoteBold ? note.indexOf(v.estimatorNoteBold) : -1;

  return (
    <div className="af-page">
      <div className="af-header">
        <div>
          <h1>Product Page Tabs</h1>
          <p>The tabs under every product (Description, Fabric Estimator, Shipping, Washcare). Rename, hide, change the words and pictures — the design stays the same.</p>
        </div>
        <div className="af-header-right">
          <span className={`af-status ${isCustom ? "custom" : ""}`}>
            {isCustom ? `● ${editedCount} field${editedCount === 1 ? "" : "s"} changed` : "● Original content"}
          </span>
          {isCustom && <button className="af-btn ghost" onClick={resetAll}>Reset to original</button>}
        </div>
      </div>

      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadImage(e.target.files[0])} />

      <div className="af-layout">
        <div className="af-form">
          {Section("description", Field({ k: "descTab", label: "Tab name" }))}

          {Section("estimator", (
            <>
              {Toggle({ k: "showEstimator", label: "Show Fabric Estimator tab" })}
              <div className="af-field">
                <div className="af-field-top"><label>Show on</label>{Edited("estimatorFor")}</div>
                <div className="pt-seg" role="group" aria-label="Show estimator on">
                  <button type="button" className={v.estimatorFor === "all" ? "on" : ""} onClick={() => set("estimatorFor", "all")}>All products</button>
                  <button type="button" className={v.estimatorFor === "fabrics" ? "on" : ""} onClick={() => set("estimatorFor", "fabrics")}>Fabrics only (sold by metre)</button>
                </div>
              </div>
              <div className="af-pair">
                {Field({ k: "estimatorTab", label: "Tab name" })}
                {Field({ k: "estimatorButton", label: "Button text" })}
              </div>
              {Field({ k: "estimatorText", label: "Text above the button", area: true, rows: 2 })}
              {Field({ k: "estimatorTitle", label: "Pop-up title" })}
              {ImageField({ k: "estimatorImage", label: "Pop-up picture", fallback: est1,
                hint: "One picture is enough — e.g. your size/metre chart. Until you upload one, the two original guides show." })}
              {Field({ k: "estimatorNote", label: "Note in the pop-up", area: true, rows: 4 })}
              {Field({ k: "estimatorNoteBold", label: "Bold words in the note", hint: "E.g. your phone number — copy it exactly from the note" })}
            </>
          ), "showEstimator")}

          {Section("shipping", (
            <>
              {Toggle({ k: "showShipping", label: "Show Shipping tab" })}
              <div className="af-pair">
                {Field({ k: "shippingTab", label: "Tab name" })}
                {Field({ k: "shippingTitle", label: "Heading" })}
              </div>
              {Field({ k: "shippingText", label: "Text", area: true, rows: 3 })}
              {ImageField({ k: "shippingImage", label: "Picture (optional)", hint: "E.g. a delivery map or courier partners" })}
            </>
          ), "showShipping")}

          {Section("washcare", (
            <>
              {Toggle({ k: "showWashcare", label: "Show Washcare tab" })}
              <div className="af-pair">
                {Field({ k: "washcareTab", label: "Tab name" })}
                {Field({ k: "washcareTitle", label: "Heading" })}
              </div>
              {ImageField({ k: "washcareImage", label: "Picture (optional)", hint: "E.g. a wash-symbols chart" })}
              <div className="af-field">
                <div className="af-field-top"><label>Care tips <span className="af-count">{v.washcareTips.length}/{L.washcareTips.items}</span></label>{Edited("washcareTips")}</div>
                <ul className="af-items">
                  {v.washcareTips.map((tip, i) => (
                    <li key={i}>
                      <span className="af-tick">•</span>
                      <input aria-label={`Tip ${i + 1}`} maxLength={L.washcareTips.each} value={tip} placeholder="Care tip"
                        onChange={(e) => set("washcareTips", v.washcareTips.map((x, j) => (j === i ? e.target.value : x)))} />
                      {counter(tip.length, L.washcareTips.each)}
                      <button type="button" aria-label={`Remove tip ${i + 1}`} onClick={() => set("washcareTips", v.washcareTips.filter((_, j) => j !== i))}>×</button>
                    </li>
                  ))}
                </ul>
                {v.washcareTips.length < L.washcareTips.items && (
                  <button type="button" className="af-btn soft small-add" onClick={() => set("washcareTips", [...v.washcareTips, ""])}>+ Add tip</button>
                )}
                <p className="af-hint">The product's own wash care text (from Edit Product) still shows above these tips.</p>
              </div>
              {Field({ k: "washcareLink", label: "Link text", hint: "Opens the full wash care guide page" })}
            </>
          ), "showWashcare")}
        </div>

        {/* Live preview — same look as the product page */}
        <aside className="af-preview-wrap">
          <div className="af-preview-label">Live preview <span>click a tab</span></div>
          <div className="pt-preview">
            <div className="pt-tabs">
              {previewTabs.map((t) => (
                <button key={t.id} type="button" className={shown === t.id ? "on" : ""} onClick={() => setPreviewTab(t.id)}>{t.label}</button>
              ))}
            </div>
            <div className="pt-body">
              {shown === "description" && (
                <div className="pt-desc">
                  {[["Description", "From each product"], ["Material", "From each product"], ["Wash Care", "From each product"]].map(([a, b], i) => (
                    <div key={a} className={i % 2 ? "" : "alt"}><b>{a}</b><span>{b}</span></div>
                  ))}
                </div>
              )}
              {shown === "estimator" && (
                <>
                  <p className="pt-text">{pv("estimatorText")}</p>
                  <span className="pt-btn">{pv("estimatorButton")}</span>
                  <div className="pt-popup">
                    <strong>{pv("estimatorTitle")}</strong>
                    <div className={`pt-popup-imgs ${v.estimatorImage ? "single" : ""}`}>
                      {v.estimatorImage ? <img src={v.estimatorImage} alt="" /> : <><img src={est1} alt="" /><img src={est2} alt="" /></>}
                    </div>
                    <p><b>Note:</b> {boldAt < 0 ? note : <>{note.slice(0, boldAt)}<b className="pt-bold">{v.estimatorNoteBold}</b>{note.slice(boldAt + v.estimatorNoteBold.length)}</>}</p>
                  </div>
                </>
              )}
              {shown === "shipping" && (
                <>
                  <div className="pt-box"><span className="pt-truck">🚚</span><div><b>{pv("shippingTitle")}</b><p>{pv("shippingText")}</p></div></div>
                  {v.shippingImage && <img className="pt-wide-img" src={v.shippingImage} alt="" />}
                </>
              )}
              {shown === "washcare" && (
                <div className="pt-box column">
                  <b className="pt-title">{pv("washcareTitle")}</b>
                  {v.washcareImage && <img className="pt-wide-img" src={v.washcareImage} alt="" />}
                  <ul>{(v.washcareTips.some((x) => x.trim()) ? v.washcareTips : D.washcareTips).filter((x) => x.trim()).map((t, i) => <li key={i}>{t}</li>)}</ul>
                  <span className="pt-link">{pv("washcareLink")}</span>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={save} what="the product page tabs"
        onDiscard={() => setConfirm({ title: "Discard your changes?", text: "The tabs go back to how they were last saved.", yes: "Discard", onYes: () => setValues(saved) })} />

      {confirm && (
        <div className="af-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="af-modal" role="dialog" aria-label={confirm.title} onClick={(e) => e.stopPropagation()}>
            <h3>{confirm.title}</h3>
            <p>{confirm.text}</p>
            <div className="af-modal-actions">
              <button className="af-btn ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="af-btn danger" onClick={() => { confirm.onYes(); setConfirm(null); }}>{confirm.yes}</button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default ProductTabsEditor;
